import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Email-code fallback for the badge code step. When LinkedIn emails a 2FA code
 * (an email-only account, or a new-device challenge on a TOTP account), the
 * seller taps "LinkedIn emailed me a code instead" — we start the backend
 * mailbox watcher and poll for the relayed code.
 *
 * `start()` POSTs to kick off the watcher; `poll()` GETs the cached result.
 * Both resolve to { status: 'watching'|'found'|'timeout'|'idle', code }.
 * `auto` starts immediately on mount (used when the account has no TOTP).
 */
export default function EmailCode({ start, poll, auto = false }) {
  const [phase, setPhase] = useState(auto ? 'watching' : 'collapsed'); // collapsed|watching|found|timeout|error
  const [code, setCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const begin = useCallback(async () => {
    setCode(null);
    setPhase('watching');
    try {
      await start();
    } catch {
      setPhase('error');
    }
  }, [start]);

  // Poll for the relayed code while watching.
  useEffect(() => {
    if (phase !== 'watching') return undefined;
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const r = await poll();
        if (r.status === 'found' && r.code) { setCode(r.code); setPhase('found'); return; }
        if (r.status === 'timeout') { setPhase('timeout'); return; }
      } catch { /* transient — keep polling */ }
      if (n < 50) pollRef.current = setTimeout(tick, 2500);
      else setPhase('timeout');
    };
    pollRef.current = setTimeout(tick, 2000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [phase, poll]);

  // Auto-start once when the account has no authenticator (mount only).
  const startedRef = useRef(false);
  useEffect(() => {
    if (auto && !startedRef.current) {
      startedRef.current = true;
      begin();
    }
  }, [auto, begin]);

  function copy() {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — code is still visible to type */ }
  }

  if (phase === 'collapsed') {
    return (
      <button type="button" className="badge-linkbtn badge-emailcode-link" onClick={begin}>
        {'LinkedIn emailed me a code instead?'}
      </button>
    );
  }
  if (phase === 'watching') {
    return <div className="badge-livecode badge-livecode--wait">Checking your email for the code…</div>;
  }
  if (phase === 'found') {
    return (
      <div className="badge-livecode">
        <button type="button" className="badge-code" onClick={copy} title="Tap to copy">{code || '••••••'}</button>
        <div className="badge-code-copy">{copied ? 'Copied!' : 'The code from your email, tap to copy'}</div>
      </div>
    );
  }
  // timeout | error
  return (
    <div className="badge-livecode badge-livecode--wait">
      {"No email code yet, "}
      <button type="button" className="badge-linkbtn" onClick={begin}>check again</button>.
    </div>
  );
}
